/**
 * HelpAgent — fallback for general Q&A. Wraps the existing
 * `generateAIContent` chat path that previously lived inline in
 * `CoachSteve.tsx`. The Rookie chat-message budget gate and usage counting
 * are owned centrally by the orchestrator (`CoachSteve.tsx`'s `handleSend`),
 * not by this or any other individual agent.
 */

import { generateAIContent } from '../../../api/ai';
import type { AgentRunContext, AgentRunResult, CoachSteveAgent } from './types';

const SYSTEM_PROMPT_FOOTER = `

**Important — creation actions (use these exact phrases when directing the user):**
- **Add a drill to a dated practice:** say e.g. "add a [drill name] drill to tomorrow's practice" → the app adds it or creates the practice; do **not** claim you are creating plans or adding drills yourself from this chat path.
- **Practice plan:** say "create a practice plan" → I create a practice and can generate a plan.
- **Game plan** (Football only): say "create a game plan" → I open the game plan flow.
- **Game (schedule):** say "create a game" or "add a game" → I open the form to add a game to the schedule.
- **Formation:** Formations are created in the app under **Depth Chart** (set positions, then use the Formations button to save). I cannot create formations from chat. If they ask to create a formation, tell them to go to Depth Chart and use the Formations button to save their setup.
- **Depth chart / formations (chat):** **Football / Flag Football:** e.g. **put [First Last] at [POSITION] in [FORMATION]** or **assign #5 to RB in Spread**. **Baseball / Softball:** Coaches can ask to **set pitching / batting order / defensive spots for a specific scheduled game** — the app will confirm which game when needed and save to the **game** (same as Game detail). Other sports: starters/rotations → **Depth Chart** in the app.
- **Player:** say "add a player" or "add [First Last]" (with position groups) → I add them to the roster.
- **Injury status:** say "mark [player] out" or "set #5 as questionable" etc. → I update injury status.

Do not suggest "add a player" when they asked for a formation, game, game plan, or practice. Football/Flag: **put [player] at [position] in [formation]**. Baseball/Softball: pitching, lineup, and fielding for a calendar game are handled by the dedicated agent (they may need to pick the game). Other sports: Depth Chart. Use ** for bold. Keep replies focused and friendly.`;

/** Build the system prompt the help agent uses to answer general questions. */
export function buildHelpSystemPrompt(ctx: AgentRunContext): string {
  const teamContext = ctx.team
    ? `Current team: ${ctx.team.name} (${ctx.team.sport}${ctx.team.ageLevel ? `, ${ctx.team.ageLevel}` : ''}).`
    : 'No team selected yet.';
  const rosterBlock = ctx.teamContextForAgent
    ? `\n\n${ctx.teamContextForAgent}\n\nUse only the LIVE TEAM DATA and GAME PERFORMANCE (if present) above to answer questions about lineups, positions, stats, and roster decisions. Reference specific players by name or number. When game performance data is present, use it to optimize lineup suggestions (e.g. high OBP at top, power in the middle) and pitching decisions (IP, K, ERA, WHIP, runs allowed). Both batting and pitching stats are included when recorded. Do not invent players or stats. The coach can apply suggestions in the app via Depth Chart or Roster.`
    : '';
  return `You are Coach Steve, a helpful youth sports coaching assistant. Answer the user's question concisely and practically. ${teamContext}${rosterBlock}${SYSTEM_PROMPT_FOOTER}`;
}

/**
 * Run the general-question chat path. Mirrors the previous fall-through path.
 *
 * `extraSystemContext` — optional one-liner injected at the end of the system
 * prompt (e.g. "Game context: vs New Lenox on 2026-05-16") so in-agent callers
 * can surface situational facts without polluting the user-message turn.
 */
export async function runHelpAgent(
  text: string,
  ctx: AgentRunContext,
  extraSystemContext?: string
): Promise<AgentRunResult> {
  try {
    if (!ctx.userId) {
      ctx.addMessage('assistant', 'Please sign in to chat with Coach Steve.');
      ctx.setIsLoading(false);
      return { kind: 'handled' };
    }
    const systemPrompt =
      buildHelpSystemPrompt(ctx) +
      (extraSystemContext ? `\n\n${extraSystemContext}` : '');

    // Pass the full back-and-forth as the conversation so the LLM always
    // has complete context. Only append `text` as a final user turn when
    // the conversation history doesn't already end with a user message —
    // in agent-to-agent calls (e.g. from depthChart) the current intent is
    // already visible through the history and `text` may be stale.
    const conversationMessages = ctx.recentMessages.slice(-12).map((m) => ({
      role: m.role as 'user' | 'assistant',
      content: m.content,
    }));
    const historyEndsWithUser =
      conversationMessages.length > 0 &&
      conversationMessages[conversationMessages.length - 1].role === 'user';
    const apiMessages = [
      { role: 'system' as const, content: systemPrompt },
      ...conversationMessages,
      ...(historyEndsWithUser ? [] : [{ role: 'user' as const, content: text }]),
    ];
    const reply = await generateAIContent(apiMessages, { timeout: 20000 });
    ctx.addMessage('assistant', reply.trim());
    return { kind: 'handled' };
  } catch (err) {
    console.error(err);
    ctx.addMessage(
      'assistant',
      "Something went wrong answering that. Try again, or ask me to **create a practice plan**, **create a game plan**, or **create a game** and I'll walk you through it."
    );
    return { kind: 'error', error: String(err) };
  } finally {
    ctx.setIsLoading(false);
  }
}

export const helpAgent: CoachSteveAgent = {
  name: 'help',
  description:
    "Fallback general-question agent. Uses the live team/roster context to answer coaching questions when no specific intent matches.",
  applicableSports: 'all',
  match: () => true, // Help is the catch-all fallback.
  async handle(text, ctx): Promise<AgentRunResult> {
    return runHelpAgent(text, ctx);
  },
};
